import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { motion } from "framer-motion";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { Minus, Plus, MapPin, Users, Calendar, Check, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi, useApi, CATEGORY_LABELS, formatDate, formatTime, priceLabel } from "@/lib/api";
import { GoldSignIn } from "@/components/Nav";
import ReviewSection from "@/components/ReviewSection";
import Stars from "@/components/Stars";

export default function EveningDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { isSignedIn } = useUser();
  const [seats, setSeats] = useState(1);
  const [paying, setPaying] = useState(false);

  const { data: ev, isLoading } = useQuery({
    queryKey: ["evening", id],
    queryFn: async () => (await publicApi.get(`/evenings/${id}`)).data,
  });

  if (isLoading) return <div className="pt-40 pb-40 text-center font-serif text-3xl text-stone">Lighting the candles…</div>;
  if (!ev) return <div className="pt-40 pb-40 text-center text-stone">Evening not found.</div>;

  const max = Math.min(ev.seats_available, 10);
  const total = ev.price * seats;

  const reserve = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        evening_id: ev.id,
        seats,
        origin_url: window.location.origin,
      });
      if (data.free) {
        toast.success("Your place is reserved");
        navigate("/bookings");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  const isFree = ev.price === 0;

  return (
    <div data-testid="evening-detail-page">
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden grain">
        <img src={ev.image} alt={ev.venue_name} className="absolute inset-0 w-full h-full object-cover img-duotone" />
        <div className="absolute inset-0 bg-gradient-to-t from-velvet via-velvet/40 to-velvet/20" />
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="absolute top-24 left-4 sm:left-8 lg:left-12 z-10 flex items-center gap-2 text-parchment/90 text-sm hover:text-gold transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-12">
          <span className="bg-wine text-parchment text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1">
            {CATEGORY_LABELS[ev.category] || ev.category}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-parchment mt-4 leading-tight">{ev.title}</h1>
          <p className="text-parchment/80 mt-2 text-lg">{ev.venue_name} · {ev.venue_type}</p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <div className="flex flex-wrap gap-8 text-sm text-ink-muted border-b border-stone-border pb-8">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gold" />{formatDate(ev.date)} · {formatTime(ev.date)}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" />{ev.city} <em className="text-stone not-italic">(exact address after booking)</em></span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-gold" />{ev.seats_available} of {ev.seats_total} seats left</span>
            {ev.review_count > 0 && (
              <span className="flex items-center gap-2"><Stars value={ev.avg_rating} size={14} />{ev.avg_rating} · {ev.review_count} note{ev.review_count > 1 ? "s" : ""}</span>
            )}
          </div>

          <div>
            <div className="eyebrow mb-3">The house</div>
            <p className="font-serif text-2xl text-ink leading-relaxed">{ev.story}</p>
          </div>

          {ev.notes && (
            <div data-testid="about-evening" className="bg-parchment-card border-l-2 border-gold p-6">
              <div className="eyebrow mb-3">About this evening</div>
              <p className="text-ink-muted leading-relaxed">{ev.notes}</p>
            </div>
          )}

          <div className="flex items-start gap-5 bg-parchment-card p-6 border border-stone-border">
            <img src={ev.host_avatar} alt={ev.host_name} className="w-20 h-20 object-cover img-duotone" />
            <div>
              <div className="eyebrow mb-1">Your host</div>
              <h3 className="font-serif text-2xl text-wine">{ev.host_name}</h3>
              <p className="text-sm text-ink-muted mt-2 leading-relaxed">{ev.host_bio}</p>
            </div>
          </div>

          {ev.included?.length > 0 && (
            <div>
              <div className="eyebrow mb-4">What the evening includes</div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {ev.included.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-muted">
                    <Check className="w-4 h-4 text-gold mt-1 shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-4 border-t border-stone-border">
            <ReviewSection eveningId={ev.id} />
          </div>
        </div>

        {/* Booking panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-28 card-parchment p-7" data-testid="booking-panel">
            <div className="flex items-baseline justify-between mb-6">
              <span className="font-serif text-4xl text-wine">{priceLabel(ev.price)}</span>
              <span className="text-sm text-stone">{isFree ? "accès libre" : "per seat"}</span>
            </div>

            {ev.sold_out ? (
              <div data-testid="detail-sold-out" className="text-center py-8">
                <p className="font-serif text-2xl text-wine mb-2">Complet</p>
                <p className="text-sm text-stone">This table is fully booked.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm text-ink-muted">Seats</span>
                  <div className="flex items-center gap-4">
                    <button data-testid="seat-minus" onClick={() => setSeats((s) => Math.max(1, s - 1))}
                      className="w-9 h-9 border border-stone-border flex items-center justify-center hover:border-gold transition-colors">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span data-testid="seat-count" className="font-serif text-2xl w-6 text-center">{seats}</span>
                    <button data-testid="seat-plus" onClick={() => setSeats((s) => Math.min(max, s + 1))}
                      className="w-9 h-9 border border-stone-border flex items-center justify-center hover:border-gold transition-colors">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-t border-stone-border mb-6">
                  <span className="text-sm text-ink-muted">Total</span>
                  <span data-testid="booking-total" className="font-serif text-2xl text-ink">{priceLabel(total)}</span>
                </div>

                <SignedIn>
                  <button data-testid="reserve-btn" onClick={reserve} disabled={paying}
                    className="btn-gold w-full py-4 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
                    {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> {isFree ? "Reserving…" : "Redirecting…"}</> : (isFree ? "Reserve your place" : "Reserve & pay")}
                  </button>
                </SignedIn>
                <SignedOut>
                  <GoldSignIn>
                    <button data-testid="signin-to-book-btn" className="btn-gold w-full py-4 text-sm font-semibold tracking-wide">
                      Sign in to reserve
                    </button>
                  </GoldSignIn>
                </SignedOut>
                <p className="text-xs text-stone text-center mt-4">
                  {isFree ? "Free entry — reserve to secure your place and get the details." : "Secured by Stripe · test mode. Card 4242 4242 4242 4242."}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
